import { format, formatDuration, intervalToDuration, toDate } from "date-fns";
import { isFunction, isNumber, isObject } from "lodash";

import { i18n } from "../boot/i18n";
import { functions } from "../boot/firebase";
import { httpsCallable } from "firebase/functions";

const DAY = 864e5;

export const deepFreeze = function (object) {
  if (object == null || typeof object !== "object") {
    return object;
  }

  const keys = Object.getOwnPropertyNames(object);

  for (const key of keys) {
    const value = object[key];

    if (value && isObject(value) && !isFunction(value)) {
      deepFreeze(value);
    }
  }

  return Object.freeze(object);
};

export const hashCode = (s) => {
  for (var i = 0, h = 0; i < s.length; i++)
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
};

export const getDueDate = (date, dayOffset = 0) => {
  if (date.constructor !== Date) {
    date = new Date(date).getTime();
  }
  return new Date(date.getTime() + dayOffset * DAY);
};

export const isDue = (due, dayOffset = 0) => {
  if (!due) {
    return false;
  }
  if (due.constructor !== Date) {
    due = new Date(due).getTime();
  }
  const now = new Date().getTime() + dayOffset * DAY;
  return due - now < 0;
};

export const fetchBlob = function (url) {
  return new Promise((resolve, reject) => {
    var xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.responseType = "blob";
    xhr.addEventListener("load", () => {
      resolve(xhr.response);
    });
    xhr.addEventListener("error", reject);
    xhr.send();
  });
};

export const fileAsString = function (file) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

export const fileAsBase64 = function (file) {
  return new Promise((resolve, reject) => {
    let reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(",")[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const dataURItoFile = async function (dataURI, filename, type) {
  if (!filename) {
    throw "Missing filename";
  }

  if (!type) {
    let match = dataURI.match(/^data:([^;,]+)/);
    if (match && match[1]) {
      type = match[1];
    } else {
      throw "Missing mimetype";
    }
  }

  const resource = await fetch(dataURI);
  const buffer = await resource.arrayBuffer();
  return new File([buffer], filename, { type });
};

export const roundPrice = (value) => Math.round(value * 100) / 100;

export const formatPercentage = (value, decimals = 2) => {
  let factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor + "%";
};

export const formatHours = (hours) => {
  const rounded = Math.round(Number(hours || 0) * 10) / 10;
  return `${rounded}h`;
};

export const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value || 0); // Avoid printing negative zero

export const parseCurrency = (value) => {
  if (isNumber(value)) {
    return value;
  }
  if (typeof value !== "string") {
    return 0;
  }
  // Remove currency symbols, commas, and whitespace, then parse
  const cleaned = value.replace(/[$,\s]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

export const formatPO = (defaultPO, orderPO) => {
  let custom = (orderPO || "").trim();
  if (custom.startsWith(defaultPO)) {
    custom = custom.slice(defaultPO.length).replace(/^[\s\-–—/]+/, "");
  }
  return custom && custom !== defaultPO
    ? `${defaultPO} (${custom})`
    : defaultPO;
};

export const formatDurationMs = (duration) => {
  if (!duration) {
    return "";
  }
  return formatDuration(
    intervalToDuration({
      start: 0,
      end: duration,
    })
  );
};

export const getRoleSort = (role) => (a, b) => {
  let aRoles = Object.keys(a.roles);
  let bRoles = Object.keys(b.roles);
  let aIndex = aRoles.indexOf(role);
  let bIndex = bRoles.indexOf(role);
  if (aIndex < 0) {
    aIndex = 1e3 + 10 - aRoles.length;
  }
  if (bIndex < 0) {
    bIndex = 1e3 + 10 - bRoles.length;
  }
  return (
    aIndex - bIndex ||
    aRoles.length - bRoles.length ||
    (a.label < b.label ? -1 : 1)
  );
};

export const formatPlainText = (html) => {
  const div = document.createElement("DIV");
  div.innerHTML = html
    .replace(/<div>(<br\s*\/?>){0,2}/g, "<br/><br/>")
    .replace(/<br\s*\/?>/g, "<br/>\n");
  return div.innerText;
};

// Reverses vue-i18n's escapeParameterHtml (see boot/i18n.js) for text that
// is rendered as plain text rather than HTML, such as a Quasar Dialog
// message. Without this, "Brown & Brown" reads "Brown &amp; Brown".
const HTML_ENTITIES = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&#39;": "'",
  "&#x2F;": "/",
  "&#x3D;": "=",
};
export const unescapeHtml = (text) =>
  typeof text === "string"
    ? text.replace(
        /&(?:amp|lt|gt|quot|apos|#39|#x2F|#x3D);/g,
        (entity) => HTML_ENTITIES[entity]
      )
    : text;

// Comparison name for the duplicate index: "&" and "and" are the same word
// in a business name, and accents, punctuation, case and spacing separate
// nothing. Without this, "Frank Kramanak & Sons" never fuzzy-matches "Frank
// Kramanak and Sons" — a mid-string insertion scores too high for any
// threshold that stays useful.
export const normalizeContactName = (name) => {
  return (name || "")
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

export const translateRules = (rules) => {
  const translateRuleArray = (fns) =>
    fns.map((fn) => (val) => {
      const result = fn(val);
      if (result === true) return true;
      if (typeof result === "string") {
        const path = `error["${result}"]`;
        // vue-i18n 9 exposes t/te on .global, not on the instance.
        return i18n.global.te(path) ? i18n.global.t(path) : result;
      }
      return result;
    });

  const translated = {};
  for (const [key, fns] of Object.entries(rules)) {
    if (typeof fns === "function") {
      translated[key] = (...args) => translateRuleArray(fns(...args));
    } else if (Array.isArray(fns)) {
      translated[key] = translateRuleArray(fns);
    } else {
      translated[key] = fns;
    }
  }
  return translated;
};

// Firebase reports a failure as a `code` plus a message written for
// developers ("Failed to get document because the client is offline."), so
// the person is shown a plain-language message for the code instead — see
// CODE_STYLE_GUIDE.md › Error Messages Are Written for the Person Using the
// App. Firestore's codes are bare; Functions, Storage and Auth prefix theirs
// ("functions/unavailable"). A Firebase code missing from this map is still
// a failure the person can only retry: `unexpected`.
const FIREBASE_ERROR_MESSAGES = {
  aborted: "changedElsewhere",
  cancelled: "canceled",
  "deadline-exceeded": "timeout",
  "not-found": "notFound",
  "permission-denied": "noPermission",
  "resource-exhausted": "busy",
  unauthenticated: "unauthenticated",
  unavailable: "offline",
  // The createUser callable returns the Admin SDK's own error (as
  // `errorInfo`, which `call` throws), so its codes come through here too.
  "auth/email-already-exists": "emailInUse",
  "auth/expired-action-code": "linkExpired",
  "auth/invalid-action-code": "linkExpired",
  "auth/invalid-credential": "wrongPassword",
  "auth/invalid-email": "emailInvalid",
  "auth/invalid-password": "weakPassword",
  "auth/network-request-failed": "offline",
  "auth/requires-recent-login": "signInAgain",
  "auth/too-many-requests": "tooManyAttempts",
  "auth/uid-already-exists": "accountExists",
  "auth/user-disabled": "accountDisabled",
  "auth/user-not-found": "wrongPassword",
  "auth/weak-password": "weakPassword",
  "auth/wrong-password": "wrongPassword",
  "storage/canceled": "canceled",
  "storage/object-not-found": "notFound",
  "storage/retry-limit-exceeded": "offline",
  "storage/unauthenticated": "unauthenticated",
  "storage/unauthorized": "noPermission",
};

// The status codes Firestore reports bare. A bare `code` outside this list is
// not Firebase's, so it keeps the i18n lookup below.
const FIRESTORE_CODES = [
  "aborted",
  "already-exists",
  "cancelled",
  "data-loss",
  "deadline-exceeded",
  "failed-precondition",
  "internal",
  "invalid-argument",
  "not-found",
  "out-of-range",
  "permission-denied",
  "resource-exhausted",
  "unauthenticated",
  "unavailable",
  "unimplemented",
  "unknown",
];

// Errors the JavaScript engine raises on a bug ("Cannot read properties of
// undefined"). Their text is for whoever fixes the bug, not the person who
// hit it.
const BUILT_IN_ERRORS = [
  EvalError,
  RangeError,
  ReferenceError,
  SyntaxError,
  TypeError,
  URIError,
];

// What `fetch` rejects with when the request never reached the network, in
// Chrome, Safari and Firefox.
const FETCH_FAILED =
  /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)$/;

/**
 * The `error.*` i18n key to show instead of an error's own message, or null
 * when its message is fit to show: our own errors, and the text a Cloud
 * Function wrote into an HttpsError on purpose.
 */
const plainErrorKey = (error) => {
  const code = typeof error.code === "string" ? error.code : "";
  const [service, name] = code.includes("/")
    ? code.split("/", 2)
    : ["firestore", code];
  let key = null;
  if (service === "functions") {
    // A callable's own HttpsError message reaches the person as written.
    // Where the function wrote none, the SDK and firebase-functions fill in
    // the bare code or a stock phrase — an unhandled throw arrives as
    // "INTERNAL", a dropped connection as "internal", a bad sign-in as
    // "Unauthenticated" — and only those are replaced.
    const message = String(error.message || "")
      .toLowerCase()
      .replace(/[\s_]+/g, "-");
    if (
      !message ||
      message === name ||
      message === "bad-request" ||
      message === "internal-error"
    ) {
      key = FIREBASE_ERROR_MESSAGES[name] || "unexpected";
    }
  } else if (service === "auth" || service === "storage") {
    key = FIREBASE_ERROR_MESSAGES[code] || "unexpected";
  } else if (FIRESTORE_CODES.includes(name)) {
    key = FIREBASE_ERROR_MESSAGES[name] || "unexpected";
  } else if (BUILT_IN_ERRORS.some((type) => error instanceof type)) {
    key = FETCH_FAILED.test(error.message) ? "offline" : "unexpected";
  } else if (
    typeof DOMException !== "undefined" &&
    error instanceof DOMException
  ) {
    // The browser's own wording. Web Bluetooth reports a closed device
    // chooser as a NotFoundError: "User cancelled the requestDevice()
    // chooser."
    if (error.name === "AbortError" || /cancel/i.test(error.message)) {
      key = "canceled";
    } else if (error.name === "NotAllowedError") {
      key = "browserBlocked";
    } else {
      key = "unexpected";
    }
  }
  // Whatever went wrong, a device that knows it is offline says so.
  if (
    key &&
    key !== "canceled" &&
    typeof navigator !== "undefined" &&
    navigator.onLine === false
  ) {
    key = "offline";
  }
  return key;
};

export const formatError = (error) => {
  console.error(error);
  // Nothing to say — a rejection with no reason (Quasar's copyToClipboard
  // rejects with `false`), a FileReader's ProgressEvent — still says
  // something went wrong, rather than a blank toast or a crash here.
  const unexpected = i18n.global.t("error.unexpected");
  if (!error) {
    return unexpected;
  }
  if (typeof error === "string") {
    const path = `error["${error}"]`;
    if (i18n.global.te(path)) {
      return i18n.global.t(path);
    } else {
      return error;
    }
  } else if (typeof error !== "object") {
    return unexpected;
  } else {
    const plainKey = plainErrorKey(error);
    if (plainKey) {
      return i18n.global.t(`error.${plainKey}`);
    }
    if ("code" in error && i18n.global.te(`error["${error.code}"]`)) {
      return i18n.global.t(`error["${error.code}"]`);
    } else if (error.message) {
      if (i18n.global.te(`error["${error.message}"]`)) {
        return i18n.global.t(`error["${error.message}"]`);
      } else {
        return error.message;
      }
    }
    return unexpected;
  }
};

export const formatSuccess = (success) => {
  if (typeof success === "string") {
    if (i18n.global.te(`success["${success}"]`)) {
      return i18n.global.t(`success["${success}"]`);
    } else {
      return success;
    }
  }
};

export const formatWarning = (warning) => {
  if (typeof warning === "string") {
    if (i18n.global.te(`warning["${warning}"]`)) {
      return i18n.global.t(`warning["${warning}"]`);
    } else {
      return warning;
    }
  }
};

export const formatHint = (hint) => {
  if (typeof hint === "string") {
    if (i18n.global.te(`hint["${hint}"]`)) {
      return i18n.global.t(`hint["${hint}"]`);
    } else {
      return hint;
    }
  }
};

export const timestampToDate = (date) => {
  if (!date || date.constructor === Date) {
    return date;
  }

  // Lead Times stores sold dates as "yyyy/MM/dd" strings (see
  // toSoldDateString) — parse them as LOCAL dates; new Date("yyyy/MM/dd")
  // parsing is engine-dependent.
  if (typeof date === "string" && /^\d{4}\/\d{2}\/\d{2}$/.test(date)) {
    const [y, m, d] = date.split("/").map(Number);
    return new Date(y, m - 1, d);
  }

  try {
    if (date.toDate) {
      date = date.toDate(date);
    } else if (date instanceof Object && "_nanoseconds" in date) {
      date = new Date(date._seconds * 1e3 + date._nanoseconds / 1e6);
    } else {
      date = new Date(date);
      if (isNaN(date.getTime())) {
        return null;
      }
    }
  } catch (e) {
    return null;
  }

  return date || null;
};

export const toSoldDateString = (date) => {
  if (!date) return "";
  const d = date.constructor === Date ? date : new Date(date);
  if (isNaN(d)) return "";
  return format(d, "yyyy/MM/dd");
};

// Latest acknowledged ship date across a sale's live ack status entries
// (touchAck mirrors each ack's `expectedDate` onto `status.acks`; a pending
// date is null). Entries whose order no longer exists are ignored, matching
// saleStatusUtil.isLiveChild.
export const latestAckShipDate = (status) => {
  const orders = status?.orders || {};
  let latest = null;
  Object.values(status?.acks || {}).forEach((entry) => {
    if (!entry || !entry.orderID || !(entry.orderID in orders)) return;
    const date = timestampToDate(entry.expectedDate);
    if (date && !isNaN(date) && (!latest || date > latest)) latest = date;
  });
  return latest;
};

// The date that decides which year a PIPELINE sale belongs to: normally the
// soldDate, but when the latest acknowledged ship date falls in a LATER year
// the sale cannot invoice before then, so it belongs to that year's pipeline
// (sold Marvin windows today, acknowledgement says 1/10 next year → next
// year's pipeline — Justin, 2026-08-23). A pending or missing ship date
// leaves the sale in its soldDate year.
export const pipelineEffectiveDate = (soldDate, shipDate) =>
  soldDate && shipDate && shipDate.getFullYear() > soldDate.getFullYear()
    ? shipDate
    : soldDate;

export const dateToTimestamp = (date, dateOnly = false) => {
  if (date && date.constructor !== String) {
    date = format(
      date,
      i18n.global.t(
        dateOnly ? "format.date-standard" : "format.date-time-standard"
      )
    );
  }
  return date;
};

export const toStartDate = (date) => new Date(`${date}T00:00:00`);
export const toEndDate = (date) => new Date(`${date}T23:59:59.999`);

export const call = async (functionName, data, options) => {
  const callable = httpsCallable(functions, functionName, options);
  let response;
  if (options && options.signal) {
    response = await callable(data, { signal: options.signal });
  } else {
    response = await callable(data);
  }
  if ("data" in response) {
    response = response.data;
  } else if ("result" in response) {
    response = response.result;
  }
  if (response && response.errorInfo) {
    throw response.errorInfo;
  } else {
    return response;
  }
};

// extractPackingSlip reads a whole scanned document in one Gemini call, and
// extractPackingSlipBatch reads a whole truck's scan slip by slip; both are
// allowed 540 s on the server (functions/extractPackingSlip.js). The SDK's
// default of 70 s gave up on a seven-page slip while the function was still
// working, so the dock saw deadline-exceeded for an import that succeeded.
// Wait as long as the server does.
export const EXTRACT_PACKING_SLIP_TIMEOUT_MS = 540_000;

// extractAckData reads a whole acknowledgement with Gemini Pro and is allowed
// 300 s (functions/vendorShipping.js); the SDK's 70 s default gave up on a long
// ack while the function was still working and threw its answer away.
export const EXTRACT_ACK_TIMEOUT_MS = 300_000;

export const printIdenticon = (html) => {
  const win = window.open();
  win.document.write(html);
  win.print();
  win.close();
};

// Ordered quantity at or above which the progress counters stop stepping by
// one: a tap checks everything off and a right-click opens an inline quantity
// editor instead of jumping to zero.
export const largeQuantityThreshold = 10;

// Given the keys of an event's `status.loaded` map and the item IDs still part
// of the event, return the loaded keys whose item is no longer on the event and
// should therefore be cleared.
//
// Compare by item ID, not by the whole composite key. Keys are
// `${itemID}-${zone}` (or `${itemID}-${zone}@${bin}`, see SaleItem.locationID),
// and once an item is loaded its row's location flips from a warehouse zone to
// the vehicle ID. A key-based comparison therefore reads a still-present item as
// removed and zeroes its loaded count, stranding it on the truck (sale
// 03900058B). Splitting on the first "-" matches how the server reads these
// keys (parseLoadedKey in functions/deliveryLabor.js).
export const staleLoadedItemKeys = (loadedKeys, validItemIDs) => {
  const valid =
    validItemIDs instanceof Set ? validItemIDs : new Set(validItemIDs);
  return (loadedKeys || []).filter((key) => !valid.has(key.split("-")[0]));
};
